import { useState, useEffect } from 'react';
import { ChevronDown, Building2, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'vendor_selected_platform_id';

export function PlatformSelector({ 
  platforms, 
  selectedPlatformId, 
  onPlatformChange, 
  primaryPlatformId,
  canRequestMore = false,
  onRequestPlatform 
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Get selected platform info
  const selectedPlatform = platforms.find(p => p.id === selectedPlatformId);

  // Persist selection to localStorage
  useEffect(() => {
    if (selectedPlatformId) {
      localStorage.setItem(STORAGE_KEY, selectedPlatformId);
    }
  }, [selectedPlatformId]);

  // Don't render if only one platform and can't request more
  if (platforms.length <= 1 && !canRequestMore) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="gap-2 min-w-[200px] justify-between"
          data-testid="platform-selector"
        >
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[150px]">
              {selectedPlatform?.name || 'Select platform'}
            </span>
          </div>
          <ChevronDown className={cn(
            "w-4 h-4 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[240px]">
        {platforms.map((platform) => (
          <DropdownMenuItem
            key={platform.id}
            onClick={() => {
              onPlatformChange(platform.id);
              setIsOpen(false);
            }}
            className="flex items-center justify-between cursor-pointer"
            data-testid={`platform-option-${platform.id}`}
          >
            <div className="flex items-center gap-2">
              <span className="truncate">{platform.name}</span>
              {platform.id === primaryPlatformId && (
                <span className="text-xs text-muted-foreground">(Primary)</span>
              )}
            </div>
            {platform.id === selectedPlatformId && (
              <Check className="w-4 h-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
        
        {canRequestMore && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                onRequestPlatform?.();
                setIsOpen(false);
              }}
              className="text-primary cursor-pointer"
              data-testid="request-platform-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Request another platform
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Helper to get initial platform from localStorage or default
export function getInitialPlatformId(platforms, primaryPlatformId) {
  // Check URL param first
  const urlParams = new URLSearchParams(window.location.search);
  const urlPlatformId = urlParams.get('platformId');
  if (urlPlatformId && platforms.some(p => p.id === urlPlatformId)) {
    return urlPlatformId;
  }
  
  // Check localStorage
  const storedId = localStorage.getItem(STORAGE_KEY);
  if (storedId && platforms.some(p => p.id === storedId)) {
    return storedId;
  }
  
  // Use primary platform
  if (primaryPlatformId && platforms.some(p => p.id === primaryPlatformId)) {
    return primaryPlatformId;
  }
  
  // Default to first platform
  return platforms[0]?.id || null;
}
